import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import copyIcon from '@/assets/icons/copy.svg';
import doneIcon from '@/assets/icons/done.svg';
import externalLinkIcon from '@/assets/icons/external-link.svg';
import styles from './DonorDetails.module.css';

export type DonorDetailsKey = 'bank-details-israel' | 'bank-details-abroad' | 'check-deposit';

interface DonorDetailsBodyProps {
  entryKey: DonorDetailsKey;
  /** The published values of the entry, as the managed catalogue serves them. */
  details: Record<string, string>;
  fundId: number;
  /** The page on the public site that shows exactly these particulars - what a coordinator sends a donor. */
  publicPageUrl?: string;
}

/**
 * The bank and cheque particulars laid out the way the public site lays them out on /bank,
 * /bank-abroad and in its cheque popup (Chaya, 22.9: "why not the design the donor already sees"):
 * the account name up top, one card per bank, the IBAN with its own copy button, the transfer
 * report call-to-action. Same values, from the same catalogue; the one thing the portal adds is
 * the line the public site cannot print - which fund to name on the transfer.
 *
 * Sized for a laptop window (Sari, 4.10: no margin under the buttons, and the scrollbar got in the
 * way): the Israeli particulars fit without scrolling at a 650px-high window, and the two actions
 * sit in a footer pinned to the bottom of the popup, so the long abroad letter can scroll under
 * them without hiding them.
 */
const DonorDetailsBody = ({ entryKey, details, fundId, publicPageUrl }: DonorDetailsBodyProps) => {
  const { t } = useTranslation();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copy = async (key: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1800);
  };

  const CopyChip = ({ id, value, ltr }: { id: string; value: string; ltr?: boolean }) => (
    <button type="button" className={styles.copyChip} onClick={() => copy(id, value)} aria-live="polite">
      <img src={copiedKey === id ? doneIcon : copyIcon} alt="" />
      {copiedKey === id ? (ltr ? 'Copied' : t('fundSettingsDocumentsPage.details.copied')) : ltr ? 'Copy' : t('fundSettingsDocumentsPage.details.copy')}
    </button>
  );

  const BankCard = ({ name, number, branch, account }: { name: string; number: string; branch: string; account: string }) => (
    <div className={styles.card}>
      <p className={styles.cardTitle}>{name} – {number}</p>
      <p className={styles.cardLine}>
        {t('fundSettingsDocumentsPage.details.branch')} {branch}
        <span className={styles.dot} aria-hidden="true">·</span>
        {t('fundSettingsDocumentsPage.details.account')} {account}
      </p>
    </div>
  );

  const reference = (
    <div className={styles.reference}>
      {entryKey === 'check-deposit'
        ? t('fundSettingsDocumentsPage.chequeReference', { fundId })
        : t('fundSettingsDocumentsPage.transferReference', { fundId })}
    </div>
  );

  const footer = publicPageUrl && (
    <div className={styles.footer}>
      <a href={publicPageUrl} target="_blank" rel="noreferrer" className={styles.footerLink}>
        <img src={externalLinkIcon} alt="" />
        {t('fundSettingsDocumentsPage.details.openPublicPage')}
      </a>
      <button type="button" className={styles.footerLink} onClick={() => copy('page', publicPageUrl)} aria-live="polite">
        <img src={copiedKey === 'page' ? doneIcon : copyIcon} alt="" />
        {copiedKey === 'page' ? t('fundSettingsDocumentsPage.details.linkCopied') : t('fundSettingsDocumentsPage.details.copyPublicPage')}
      </button>
    </div>
  );

  if (entryKey === 'bank-details-israel') {
    return (
      <div className={styles.body}>
        {reference}
        <div className={`${styles.card} ${styles.inlineRow}`}>
          <span className={styles.cardLabel}>{t('fundSettingsDocumentsPage.details.accountName')}</span>
          <span className={styles.accountName}>{details.accountName}</span>
        </div>
        <div className={styles.twoCards}>
          <BankCard name={details.firstBankName} number={details.firstBankNumber} branch={details.firstBankBranch} account={details.firstBankAccount} />
          <BankCard name={details.secondBankName} number={details.secondBankNumber} branch={details.secondBankBranch} account={details.secondBankAccount} />
        </div>
        <div className={`${styles.card} ${styles.between}`}>
          <div className={styles.inlineRow}>
            <span className={styles.cardLabel}>{t('fundSettingsDocumentsPage.details.iban')}</span>
            <span className={styles.mono} dir="ltr">{details.iban}</span>
          </div>
          <CopyChip id="iban" value={details.iban} />
        </div>
        {footer}
      </div>
    );
  }

  if (entryKey === 'bank-details-abroad') {
    return (
      <div className={styles.body}>
        {reference}
        <div className={`${styles.card} ${styles.ltr}`} dir="ltr">
          <p className={styles.preLine}>{details.intro}</p>
          <dl className={styles.grid}>
            <dt>Bank Name:</dt><dd>{details.bankName}</dd>
            <dt>Branch:</dt><dd>{details.branchName}</dd>
            <dt>Branch no.</dt><dd>{details.branchNumber}</dd>
            <dt>Branch Address:</dt><dd>{details.branchAddress}</dd>
            <dt>ACC NAME:</dt><dd>{details.accountName}</dd>
            <dt>ACC NUMBER:</dt><dd>{details.accountNumber}</dd>
            <dt>Transfer&apos;s Reason:</dt><dd>{details.transferReason}</dd>
          </dl>
          <p className={`${styles.preLine} ${styles.muted}`}>{details.note}</p>
        </div>
        <div className={styles.twoCards}>
          <div className={styles.card} dir="ltr">
            <p className={styles.cardLabel}>IBAN</p>
            <p className={`${styles.mono} ${styles.breakAll}`}>{details.iban}</p>
            <CopyChip id="iban" value={details.iban} ltr />
          </div>
          <div className={styles.card} dir="ltr">
            <p className={styles.cardLabel}>Swift code no.</p>
            <p className={styles.mono}>{details.swift}</p>
            <CopyChip id="swift" value={details.swift} ltr />
          </div>
        </div>
        <div className={`${styles.card} ${styles.ltr}`} dir="ltr">
          <p className={styles.cardTitle}>Sincerely yours,</p>
          <p className={`${styles.preLine} ${styles.muted}`}>{details.signature}</p>
        </div>
        {footer}
      </div>
    );
  }

  return (
    <div className={styles.body}>
      {reference}
      <div className={styles.card}>
        <p className={styles.cardTitle}>{details.howTo}</p>
        <p className={styles.cardLine}>{details.where}</p>
        <div className={styles.highlight}>
          <p className={styles.highlightTitle}>{details.endorsement}</p>
          <p className={styles.cardLine}>{details.endorsementNote}</p>
        </div>
      </div>
      <div className={styles.twoCards}>
        <BankCard name={details.firstBankName} number={details.firstBankNumber} branch={details.firstBankBranch} account={details.firstBankAccount} />
        <BankCard name={details.secondBankName} number={details.secondBankNumber} branch={details.secondBankBranch} account={details.secondBankAccount} />
      </div>
      <div className={styles.card}>
        <p className={styles.cardTitle}>{t('fundSettingsDocumentsPage.details.sendConfirmation')}</p>
        <p className={styles.cardLine}>
          {t('fundSettingsDocumentsPage.details.toEmail')}{' '}
          <a href={`mailto:${details.confirmationEmail}`} className={styles.inlineLink}><bdi dir="ltr">{details.confirmationEmail}</bdi></a>
          <span className={styles.dot} aria-hidden="true">·</span>
          {t('fundSettingsDocumentsPage.details.toFax')} <bdi dir="ltr">{details.confirmationFax}</bdi>
        </p>
      </div>
      {details.cashNote && <div className={styles.note}>{details.cashNote}</div>}
      <div className={styles.bottomSpace} aria-hidden="true" />
    </div>
  );
};

export default DonorDetailsBody;
